/* eslint-disable spire/fenced-imports */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { safeColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import handleYearSelected from "@waytek/Handlers/SpendReporting/HandleYearSelected";
import loadSpendReport from "@waytek/Handlers/SpendReporting/LoadSpendReport";
import { CustomState } from "@waytek/Store/Reducers";
import { colorStrings, mediaQueriesBP } from "@waytek/Utilities/variables";
import Chart from "chart.js/auto";
import debounce from "lodash/debounce";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    summaryLabel = "summaryLabel",
    labels = "labels",
    yAxisDivisor = "yAxisDivisor",
    y1BarColor = "y1BarColor",
    y1AxisLabel = "y1AxisLabel",
    y2LineColor = "y2LineColor",
    y2AxisLabel = "y2AxisLabel",
}

const enum labels {
    month1 = "Jan",
    month2 = "Feb",
    month3 = "Mar",
    month4 = "Apr",
    month5 = "May",
    month6 = "Jun",
    month7 = "Jul",
    month8 = "Aug",
    month9 = "Sep",
    month10 = "Oct",
    month11 = "Nov",
    month12 = "Dec",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.summaryLabel]: string;
        [fields.labels]: LabelModel[];
        [fields.yAxisDivisor]: number;
        [fields.y1BarColor]: string;
        [fields.y1AxisLabel]: string;
        [fields.y2LineColor]: string;
        [fields.y2AxisLabel]: string;
    };
}

interface LabelModel {
    fields: {
        id: number;
        name: string;
    };
}

const mapStateToProps = (state: CustomState) => {
    const spendReportingState = state.spendReportingState;
    const yearSelected = spendReportingState.yearSelected ?? new Date().getFullYear();
    const spendReport = spendReportingState.spendReport[yearSelected];
    const years = spendReport?.availableYears ?? [];

    const summaryValue = spendReportingState.spendReport?.[yearSelected]?.yearToDateSalesDisplay ?? "$0";

    return {
        isAuthenticated: getSession(state).isAuthenticated,
        isLoading: spendReportingState.isLoading,
        summaryValue,
        yearSelected,
        spendReport,
        years,
    };
};

const mapDispatchToProps = {
    handleYearSelected,
    loadSpendReport,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface SpendReportingChartStyles {
    wrapper?: InjectableCss;
    titleLabel?: TypographyPresentationProps;
    header?: InjectableCss;
    summaryLabel?: TypographyPresentationProps;
    summaryValue?: TypographyPresentationProps;
    yearSelectorWrapper?: InjectableCss;
    yearSelector?: SelectPresentationProps;
    loadingWrapper?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
}

export const spendReportingChartStyles: SpendReportingChartStyles = {
    wrapper: {
        css: css`
            border: 1px solid ${getColor(colorStrings.commonBorder)};
            display: flex;
            flex-direction: column;
            gap: 24px;
            padding: 40px;
            position: relative;

            ${mediaQueriesBP.maxMD} {
                gap: 16px;
                padding: 16px;
            }
        `,
    },
    titleLabel: {
        variant: "h2",
        transform: "uppercase",
        css: css`
            margin: 0;
        `,
    },
    header: {
        css: css`
            display: flex;
            gap: 24px;

            ${mediaQueriesBP.maxMD} {
                flex-direction: column;
                gap: 6px;
            }

            ${mediaQueriesBP.minMD} {
                align-items: center;
                justify-content: space-between;
            }
        `,
    },
    summaryValue: {
        weight: 700,
    },
    yearSelectorWrapper: {
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    loadingWrapper: {
        css: css`
            align-items: center;
            background-color: ${safeColor(getColor(colorStrings.commonGlobalWhite)).rgb().alpha(0.06).string()};
            display: inline-flex;
            height: 100%;
            justify-content: center;
            position: absolute;
            right: 0;
            top: 0;
            width: 100%;
        `,
    },
};

const styles = spendReportingChartStyles;

const SpendReportingChart: React.FC<Props> = ({
    shellContext: { isEditing },
    fields: { title, summaryLabel, yAxisDivisor, y1BarColor, y1AxisLabel, y2LineColor, y2AxisLabel, labels: months },
    isAuthenticated,
    isLoading,
    summaryValue,
    yearSelected,
    spendReport,
    years,
    handleYearSelected,
    loadSpendReport,
}) => {
    const [isMobile, setIsMobile] = React.useState(false);
    const [chartData, setChartData] = React.useState<Chart>();

    if (isEditing || !isAuthenticated) {
        return null;
    }

    const canvasRef = React.createRef<HTMLCanvasElement>();

    const calculateAspectRatio = isMobile ? 3 / 4 : 21 / 9;

    const handleCharResize = () => {
        setIsMobile(window.matchMedia("(max-width: 425px)").matches);
    };

    const callbackMonthLabels = (_: any, idx: number): string => {
        if (isMobile && idx % 2 !== 0) {
            return "";
        }

        const label = months.find(item => item.fields.id === idx + 1);
        if (label) {
            return label.fields.name;
        }
        return `${idx + 1}`;
    };

    const callbackAmountLabel = (value: any) => {
        if (typeof value !== "number") {
            return value;
        }

        if (value === 0) {
            return "$0";
        }
        if (value >= 1000000) {
            const label = parseFloat(`${value / 1000000}`).toFixed(2);
            return `$${label}M`;
        }

        const label = parseFloat(`${value / 1000}`);

        if (value >= 10000) {
            return `$${label.toFixed(0)}K`;
        }
        return `$${label.toFixed(1)}K`;
    };

    const buildDatasets = (y1Data: number[], y2Data: number[]): any[] => {
        return [
            {
                label: y1AxisLabel,
                data: y1Data,
                backgroundColor: y1BarColor,
                yAxisID: "y1",
                order: 1,
            },
            {
                label: y2AxisLabel,
                data: y2Data,
                backgroundColor: y2LineColor,
                borderColor: y2LineColor,
                borderWidth: 1,
                type: "line",
                yAxisID: "y2",
                order: 0,
            },
        ];
    };

    const buildChartData = (
        y1Data: number[] = [],
        maxBarsData = +(yAxisDivisor ?? 10),
        stepBarSize = 1,

        y2Data: number[] = [],
        maxLinesData = +(yAxisDivisor ?? 10),
        stepLineSize = 1,
    ) => {
        const labels: string[] = [];
        for (let i = 1; i <= 12; i++) {
            const label = months.find(item => item.fields.id === i);
            if (label) {
                labels.push(label.fields.name);
            } else {
                labels.push(`${i}`);
            }
        }

        setTimeout(() => {
            const chartBox = document.querySelector(".spend-report__chart");
            const chartDataEl = new Chart(chartBox as any, {
                type: "bar",
                data: {
                    labels,
                    datasets: buildDatasets(y1Data, y2Data),
                },
                options: {
                    aspectRatio: calculateAspectRatio,
                    plugins: {
                        legend: {
                            position: "bottom",
                            align: "start",
                            onClick: () => {},
                            labels: {
                                sort: (a: { datasetIndex?: number }, b: { datasetIndex?: number }, _: unknown) =>
                                    (a.datasetIndex ?? 0) - (b.datasetIndex ?? 0),
                            },
                        },
                    },
                    scales: {
                        x: {
                            ticks: {
                                callback: callbackMonthLabels,
                            },
                        },
                        y1: {
                            beginAtZero: true,
                            position: "left",
                            min: 0,
                            max: maxBarsData,
                            ticks: {
                                stepSize: stepBarSize,
                                callback: callbackAmountLabel,
                            },
                        },
                        y2: {
                            beginAtZero: true,
                            grid: {
                                drawOnChartArea: false,
                            },
                            min: 0,
                            max: maxLinesData,
                            position: "right",
                            ticks: {
                                stepSize: stepLineSize,
                            },
                        },
                    },
                },
            });

            setChartData(chartDataEl);
        }, 100);
    };

    React.useEffect(() => {
        loadSpendReport({});

        setIsMobile(window.matchMedia("(max-width: 425px)").matches);
        window.addEventListener("resize", debounce(handleCharResize, 500));

        buildChartData();
    }, []);

    React.useEffect(() => {
        if (spendReport != null) {
            const barsData: number[] = (spendReport.spendReportList ?? []).map(item => item.ordersAmount ?? 0);
            const maxBarsData = Math.max(...barsData);

            const linesData = (spendReport.spendReportList ?? []).map(item => item.ordersPlaced ?? 0);
            let maxLinesData = Math.max(...linesData);
            while (maxLinesData % (yAxisDivisor ?? 10) !== 0) {
                maxLinesData++;
            }

            if (chartData) {
                chartData.data.datasets[0].data = barsData;
                chartData.data.datasets[1].data = linesData;

                chartData.options.scales!.y1!.max = maxBarsData;
                (chartData.options.scales!.y1!.ticks as any)!.stepSize = maxBarsData / yAxisDivisor;
                chartData.options.scales!.y1!.ticks!.callback = (value: any) => callbackAmountLabel(value);

                chartData.options.scales!.y2!.max = maxLinesData;
                (chartData.options.scales!.y2!.ticks as any)!.stepSize = maxLinesData / yAxisDivisor;

                chartData.update();
            } else {
                buildChartData(
                    barsData,
                    maxBarsData,
                    maxBarsData / yAxisDivisor,
                    linesData,
                    maxLinesData,
                    maxLinesData / yAxisDivisor,
                );
            }
        }
    }, [spendReport]);

    React.useEffect(() => {
        if (chartData) {
            chartData.options.aspectRatio = calculateAspectRatio;
            chartData.resize();
            chartData.update();
        }
    }, [isMobile]);

    const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        handleYearSelected({ yearSelected: +event.target.value });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.titleLabel}>{title}</Typography>

            <StyledWrapper {...styles.header}>
                <Typography {...styles.summaryLabel}>
                    {summaryLabel} <Typography {...styles.summaryValue}>{summaryValue}</Typography>
                </Typography>

                <StyledWrapper {...styles.yearSelectorWrapper}>
                    <StyledWrapper>
                        <Select {...styles.yearSelector} value={yearSelected} onChange={handleYearChange}>
                            {years.length === 0 && <option value={yearSelected}>{yearSelected}</option>}
                            {years.length > 0 &&
                                years.map(value => (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                ))}
                        </Select>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <canvas className="spend-report__chart" ref={canvasRef}></canvas>

            {isLoading && (
                <StyledWrapper {...styles.loadingWrapper}>
                    <LoadingSpinner {...styles.loadingSpinner} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(SpendReportingChart) as any) as any,
    definition: {
        group: "My Account",
        displayName: "Spend Reporting Chart",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Spending Report",
                isRequired: true,
            },
            {
                name: fields.summaryLabel,
                displayName: "Sales Summary",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Year to Date Sales:",
                isRequired: true,
            },
            {
                name: fields.yAxisDivisor,
                displayName: "Y Axis Divisor",
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
            },
            {
                name: fields.y1BarColor,
                editorTemplate: "ColorPickerField",
                displayName: "Bar chart color",
                defaultValue: "#2E5996",
                fieldType: "General",
            },
            {
                name: fields.y1AxisLabel,
                displayName: "Left Axis Label",
                editorTemplate: "TextField",
                defaultValue: "Purchases ($)",
                fieldType: "Translatable",
            },
            {
                name: fields.y2LineColor,
                editorTemplate: "ColorPickerField",
                displayName: "Line chart color",
                defaultValue: "#2E4f26",
                fieldType: "General",
            },
            {
                name: fields.y2AxisLabel,
                displayName: "Right Axis Label",
                editorTemplate: "TextField",
                defaultValue: "# of Orders Placed",
                fieldType: "Translatable",
            },
            {
                name: fields.labels,
                displayName: "Months Labels",
                editorTemplate: "ListField",
                defaultValue: [
                    { fields: { id: 1, name: labels.month1 } },
                    { fields: { id: 2, name: labels.month2 } },
                    { fields: { id: 3, name: labels.month3 } },
                    { fields: { id: 4, name: labels.month4 } },
                    { fields: { id: 5, name: labels.month5 } },
                    { fields: { id: 6, name: labels.month6 } },
                    { fields: { id: 7, name: labels.month7 } },
                    { fields: { id: 8, name: labels.month8 } },
                    { fields: { id: 9, name: labels.month9 } },
                    { fields: { id: 10, name: labels.month10 } },
                    { fields: { id: 11, name: labels.month11 } },
                    { fields: { id: 12, name: labels.month12 } },
                ],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                hideAdd: true,
                hideEdit: false,
                hideDelete: true,
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
